import { supabaseClient, useBoothInfo, useUser } from '@auth-v2';
import { InfiniteData, useMutation, UseMutationOptions, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
    Activity,
    ACTIVITY_MODES,
    ApiConfig,
    closeActivity,
    createActivityV2,
    fetchActivityById,
    getCurrentActivity,
    markActivityAsDeleted,
    patchCurrentActivity,
    updateActivity,
    UpdateActivityParams,
    updateActivityTags,
} from '@data';
import { useEnsureAtomValue, userAtom } from '@store';

import { queryClient } from '../config/client';
import { queryKeys } from '../queryKeys';

export const useActivity = (activityId: number) => {
    const { data: user } = useUser();

    return useQuery({
        queryKey: queryKeys.activities.one(activityId),
        queryFn: () => fetchActivityById(supabaseClient, activityId ?? -1),
        enabled: !!user && activityId > 0,
    });
};

type ActivityTagsUpdatePayload = { activityId: number; tagIds: number[] };

export const useUpdateActivityTags = () => {
    return useMutation({
        mutationFn: async ({ tagIds, activityId }: ActivityTagsUpdatePayload) => {
            await updateActivityTags(supabaseClient, activityId, tagIds);
            return activityId;
        },
        onSuccess: (activityId) => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.activities.one(activityId),
            });
        },
    });
};

type ActivityUpdatePayload = { activityId: number } & UpdateActivityParams;

type UpdateActivityClubOptions = Omit<
    UseMutationOptions<number, Error, ActivityUpdatePayload>,
    'mutationFn' | 'onSuccess'
>;

export const useUpdateActivity = (options?: UpdateActivityClubOptions) => {
    const { data: user } = useUser();

    return useMutation({
        ...options,
        mutationFn: async ({ activityId, ...updateParams }: ActivityUpdatePayload) => {
            await updateActivity(activityId, updateParams);
            return activityId;
        },
        onSuccess: (activityId) => {
            return Promise.all([
                queryClient.invalidateQueries({
                    queryKey: queryKeys.activities.one(activityId),
                }),
                queryClient.invalidateQueries({
                    queryKey: queryKeys.activities.current(user?.id ?? ''),
                }),
            ]);
        },
    });
};

const MODE_MESSAGE = {
    [ACTIVITY_MODES.BALLLESS]: 'Ball-less mode enabled',
    [ACTIVITY_MODES.DEFAULT]: 'Default mode enabled',
    [ACTIVITY_MODES.LIVE_FEEDBACK]: 'Live Feedback mode enabled',
};

export function useUpdateCurrentActivityMode() {
    const { data: user } = useUser();
    return useMutation({
        mutationFn: (mode: keyof typeof ACTIVITY_MODES) => patchCurrentActivity(mode),
        onSuccess: ({ data }) => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.activities.current(user?.id ?? ''),
            });
            if (data.mode) toast.success(MODE_MESSAGE[data.mode] ?? 'Success');
        },
    });
}

/**
 * @boothOnly
 * Warning this is a booth only function, if you call it in any other context it will fail
 * @returns query hook for activity
 */
export const useReadOnlyCurrentActivity = () => {
    const { data: booth } = useBoothInfo();
    const { data: user } = useUser();

    return useQuery({
        queryKey: queryKeys.activities.current(user?.id ?? ''),
        queryFn: async (): Promise<Activity | null> => {
            if (!booth || !user) {
                throw new Error('No incorrect user credentials');
            }

            if (!booth.beastEndpoint) {
                throw Error('beast endpoint not set');
            }

            return getCurrentActivity();
        },
        enabled: !!user && !!booth && ApiConfig.isBooth,
        retryOnMount: false,
    });
};

/**
 * @boothOnly
 * Warning this is a booth only function, if you call it in any other context it will fail
 * @returns mutation hook for activity
 */
type CreateActivityOptions = Omit<UseMutationOptions<Activity, Error, number>, 'mutationFn' | 'onSuccess'>;

export const useCreateActivity = (options?: CreateActivityOptions) => {
    const { data: booth } = useBoothInfo();
    const { data: user } = useUser();

    return useMutation({
        ...options,
        mutationFn: async (clubId: number) => {
            if (!booth || !user) {
                throw new Error('No incorrect user credentials');
            }

            return await createActivityV2(clubId);
        },
        onSuccess: (activity) => {
            queryClient.setQueryData(queryKeys.activities.one(activity.id), activity);
            queryClient.setQueryData(queryKeys.activities.current(user?.id ?? ''), activity);
        },
    });
};

export const useDeleteActivity = (activeUser: string | undefined) => {
    return useMutation({
        mutationFn: async (activityId: number) => {
            return markActivityAsDeleted(activityId);
        },
        onSuccess: (deletedActivityId) => {
            if (!activeUser) return;

            queryClient.setQueriesData(
                { queryKey: queryKeys.activities.paged(activeUser), exact: false },
                (activities: InfiniteData<Activity[]>) => {
                    if (!activities) {
                        return activities;
                    }

                    return {
                        ...activities,
                        pages: activities.pages.map((page) =>
                            page.filter((activity) => activity.id !== deletedActivityId),
                        ),
                    };
                },
            );

            queryClient.invalidateQueries({
                queryKey: queryKeys.activities.count(activeUser),
            });
        },
    });
};

export const useCloseActivity = () => {
    const { id: userId } = useEnsureAtomValue(userAtom);
    return useMutation({
        mutationFn: async (activityId: number) => {
            return closeActivity(activityId);
        },
        onSuccess: async (activityId) => {
            const queryKey = queryKeys.activities.current(userId);
            if (queryClient.getQueryData<Activity | null>(queryKey)?.id === activityId) {
                await queryClient.cancelQueries({ queryKey });
                queryClient.setQueryData<Activity | null>(queryKey, (activity) =>
                    activity?.id === activityId ? null : activity,
                );
            }
            queryClient.invalidateQueries({
                queryKey: queryKeys.activities.count(userId),
            });
        },
    });
};
